import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { Button as UiButton } from '@/common/components/ui/button'
import { ChartRenderer } from '@/common/components/ChartRenderer'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ShowHeader } from '@/common/components/ShowHeader'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { ChartDataTransformer } from '@/common/services/chartDataTransformer'
import { Mapper } from '@/common/services/mapper'
import { ChartData } from '@/common/types/chart.type'
import { useDashboardData } from '@/features/company/hooks/useDashboardData'
import { Link } from 'react-router-dom'
import { useMineCompany } from '../../hooks/useMineCompany'
import { billingBillStatusOptions } from '../../../billingBills/constants/billingBillStatus.constant'
import { ArrowUpRight, X } from 'lucide-react'
import { PaymentAdvancesIndicator } from '../../components/PaymentAdvances/PaymentAdvancesIndicator'
import usePaymentAdvancesTotalAvailable from '../../hooks/usePaymentAdvancesTotalAvailable'
import { TotalAvailableTransformer } from '@/common/services/totalAvailableTransformer'
import { useMemo, useState } from 'react'
import { startOfMonth } from 'date-fns'

const Company = () => {
  const { filterParams, setFilterParams } = useFilterParams()
  const inProcessStatus = billingBillStatusOptions.find(({ value }) => value === 'in_process');

  const [isAllTime, setIsAllTime] = useState(false)

  const defaultDate = useMemo(() => {
    const today = new Date()
    return {
      from: startOfMonth(today),
      to: today
    }
  }, [])

  const effectiveDate = isAllTime 
    ? undefined
    : (filterParams?.date?.from ? filterParams.date : defaultDate)

  const effectiveFilterParams = {
    ...filterParams,
    date: effectiveDate
  }

  const { date, formattedDate } = useFilterDate(effectiveFilterParams)

  const urlToPaymentAdvancesTotalAvailable = new URLSearchParams({
    'status[value]': inProcessStatus?.value || '',
    'status[label]': inProcessStatus?.label || '',
  });

  const { data: company } = useMineCompany()
  const {
    data: dashboardData,
    isFetching,
    error: dashBoardError = {} as any,
    refetch: refetchDashboard,
  } = useDashboardData<ChartData[]>({
    filters: {
      startDate: formattedDate.from,
      endDate: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: ChartDataTransformer.chartTransformer(),
    }),
  })

  const { data: totalAvailableData } = usePaymentAdvancesTotalAvailable({
    mapper: TotalAvailableTransformer.withDaysRemaining()
  });

  const maxInterestedAvailable = Number(totalAvailableData?.max_interested_available_to_be_advanced || 0);

  return (
    <div className="flex flex-col max-h-[calc(100vh-64px)] overflow-y-auto">
      <ShowHeader
        logoUrl={company?.general_information.company_logo?.url}
        title={company?.general_information.fantasy_name}
        buttons={
          <div className="flex">
            {!!maxInterestedAvailable && (
              <Link to={`/billing-bills?${urlToPaymentAdvancesTotalAvailable.toString()}`}>
                <Button name='total_available' operationKlassToCheckPermission="Establishment::Billing::PaymentAdvances::TotalAvailable" className="bg-[#198f51] hover:bg-[#157a45]">
                  Antecipar valor <ArrowUpRight className='w-4 h-4' />
                </Button>
              </Link>
            )}

            <Link to="/company/mine">
              <Button variant="outline-disabled">Ver perfil</Button>
            </Link>
          </div>
        }
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-192px)]"
        statusCode={dashBoardError?.status}
        refetchFunction={refetchDashboard}
      >
        <div className="flex flex-col gap-6">
          <div className="flex justify-between">
            <div className='flex gap-1'>
              <DatePickerWithRange 
                date={date} 
                setDate={(date) => {
                  setIsAllTime(false)
                  setFilterParams((params) => ({ ...params, date }))
                }} 
              />
              {!filterParams?.isAllTime && (
                <UiButton 
                  variant={"badge"}
                  size={"badge"}
                  className='bg-white-100 rounded-xl w-8 max-w-8 p-0'
                  onClick={() => {
                    setIsAllTime(true)
                    setFilterParams((params) => ({ ...params, date: undefined }))
                  }}
                >
                  <X className='max-w-4' />
                </UiButton>
              )}
            </div>

            {!!maxInterestedAvailable && <PaymentAdvancesIndicator totalAvailableData={totalAvailableData} />}
          </div>

          {isFetching ? (
            <Box>Carregando...</Box>
          ) : (
            <div className="grid grid-cols-12 gap-6">
              {dashboardData?.map((chartData) => (
                <ChartRenderer key={chartData.title} chartData={chartData} className="pb-12 max-h-80 [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-transparent" />
              ))}
            </div>
          )}
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default Company
